import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { api, pad } from '../../lib/api';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { Label } from '../../components/u99/Label';
import Colophon, { Byline, KindEyebrow } from '../../components/u99/Colophon';

const groupByPart = (chapters) => {
  const out = [];
  chapters.forEach((c) => {
    const last = out[out.length - 1];
    if (last && last.part === c.part) last.items.push(c);
    else out.push({ part: c.part, part_title: c.part_title, items: [c] });
  });
  return out;
};

export default function BookPage() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/books/${slug}`).then((r) => { setBook(r.data); document.title = `${r.data.title} — ${r.data.author}`; }).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <main className="pt-40 pb-40 text-center px-6" data-testid="book-not-found">
        <p className="font-mono-precision text-xs tracking-[0.3em] text-amber-300">NOT ON THE SHELF</p>
        <h1 className="font-celestial text-3xl text-slate-100 mt-4">This book is not published</h1>
        <Link to="/u99/documents" className="inline-block mt-8 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300">← Documents</Link>
      </main>
    );
  }
  if (!book) return <div className="pt-48 pb-48 text-center font-mono-precision text-xs tracking-[0.3em] text-slate-600">OPENING…</div>;

  const first = book.chapters[0];
  return (
    <main className="min-h-screen" data-testid="book-page">
      <header className="relative pt-32 pb-14 lg:pt-44 lg:pb-24 nebula-mesh overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Link to="/u99/documents" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-amber-300 transition-colors" data-testid="book-back-link"><ArrowLeft size={12} /> Documents</Link>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="mt-10 flex flex-wrap items-center gap-4">
              <KindEyebrow kind="book" />
              <Label id={book.label} size="xs" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-celestial text-4xl sm:text-5xl lg:text-7xl font-light text-slate-100 leading-[1.02]" data-testid="book-title">{book.title}</motion.h1>
            {book.subtitle && <p className="mt-4 font-celestial text-xl sm:text-2xl text-amber-200/80 italic" data-testid="book-subtitle">{book.subtitle}</p>}
            <Byline author={book.author} />
            {book.description && <p className="mt-8 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl" data-testid="book-description">{book.description}</p>}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={`/u99/book/${book.slug}/${first.slug}`} className="inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] px-6 py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#02040a] transition-colors" data-testid="book-start-reading"><BookOpen size={14} /> Begin reading</Link>
              <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="book-progress">{book.chapters_ready}/{book.chapters.length} chapters transcribed · {book.word_count.toLocaleString()} words</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, rotateY: -20 }} animate={{ opacity: 1, rotateY: -12 }} transition={{ duration: 1.2, delay: 0.3 }} className="hidden lg:block lg:col-span-4 [perspective:1200px]" aria-hidden="true">
            <div className="relative aspect-[2/3] max-w-[260px] ml-auto rounded-r-xl rounded-l-sm border border-amber-400/30 bg-gradient-to-br from-[#0b0f1c] via-[#060a14] to-[#02040a] shadow-[0_40px_80px_-20px_rgba(245,158,11,0.25)] p-8 flex flex-col justify-between" style={{ transform: 'rotateY(-12deg)' }}>
              <span className="font-mono-precision text-[9px] uppercase tracking-[0.3em] text-amber-300/70">Kyle Hinton</span>
              <div>
                <p className="font-celestial text-2xl leading-tight text-slate-100">{book.title}</p>
                <p className="mt-3 font-celestial text-sm italic text-amber-200/70">{book.subtitle}</p>
              </div>
              <span className="w-10 h-px bg-amber-400/60" />
            </div>
          </motion.div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-28 lg:pb-40">
        <Reveal><Eyebrow>Contents</Eyebrow></Reveal>
        <div className="border-t border-white/[0.07]" data-testid="book-contents">
          {groupByPart(book.chapters).map((g, gi) => (
            <div key={g.part || 'intro'} className="grid lg:grid-cols-12 gap-x-10 border-b border-white/[0.07] py-8">
              <Reveal className="lg:col-span-3 mb-4 lg:mb-0">
                {g.part ? (<><p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-amber-300">{g.part_title}</p><p className="mt-2 font-celestial text-3xl text-slate-600">{g.part}</p></>) : <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500">Opening</p>}
              </Reveal>
              <div className="lg:col-span-9">
                {g.items.map((c, i) => (
                  <Reveal key={c.slug} delay={i * 0.05}>
                    <Link to={`/u99/book/${book.slug}/${c.slug}`} className="group flex items-baseline gap-5 py-3.5 border-b border-white/[0.04] last:border-0" data-testid={`chapter-link-${c.slug}`}>
                      <span className="font-celestial text-slate-600 group-hover:text-amber-300 transition-colors w-8">{pad(c.number)}</span>
                      <span className="flex-1 font-celestial text-xl text-slate-200 group-hover:text-amber-100 transition-colors">{c.title}</span>
                      <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-600">{c.word_count ? `${c.word_count.toLocaleString()} w` : 'pending'}</span>
                      <ArrowRight size={14} className="text-slate-600 group-hover:text-amber-300 group-hover:translate-x-1 transition-all" />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Colophon kind="book" title={`${book.title}: ${book.subtitle}`} author={book.author} date={book.updated_at} words={book.word_count} verified={false} path={`/u99/book/${book.slug}`} />
      </section>
    </main>
  );
}
